import axios from "axios";
import {
  PaymentLinkResponse,
  PaymentMessageData,
  PreAuthorizationData,
  MarketplaceExtensionResponse,
  LinkedAccountResponse,
  ProductPaymentRequest,
} from "./types";
import { currentFeatures, endpoints } from "@/configs/Settings";
import { getAuthToken } from "@/services/auth/OAuthApplication";
/**
 * Serviço para comunicação com endpoints do marketplace
 */
export class MarketplaceService {
  private readonly apiUrl = `${
    import.meta.env.VITE_ENDPOINT_BILLING
  }/marketplace`;

  /**
   * Header de autenticação padrão do legado.
   * Mantém o mesmo comportamento de serviços FHIR (ex.: GetSlot):
   * - quando existir token do usuário, usa ele
   * - caso contrário, usa token de aplicação
   */
  private async getAuthorizationHeader() {
    return await getAuthToken();
  }

  /**
   * Registra uma solicitação de pré-autorização antes do redirecionamento
   * @param data Dados de pré-autorização contendo CPF e stateId
   */
  async registerPreAuthorization(data: PreAuthorizationData): Promise<void> {
    try {
      await axios.post(
        `${this.apiUrl}/preAuthorization`,
        { ...data, origin: currentFeatures.defaultApp ? "fluxmed" : "fluxvet" },
        {
          headers: {
            Authorization: await this.getAuthorizationHeader(),
          },
        }
      );
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error("Erro ao registrar pré-autorização:", error.message);
        if (error.response) {
          console.error("Detalhes do erro:", error.response.data);
        }
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        console.error("Ocorreu um erro desconhecido:", error);
        throw new Error(
          "Ocorreu um erro desconhecido ao registrar pré-autorização"
        );
      }
    }
  }

  /**
   * Obtém o link de pagamento usando apenas o ID do slot
   * @param slotId ID do slot
   * @returns Link de pagamento
   * @throws Error se não for possível obter o link
   */
  async getPaymentLinkBySlotId(slotId: string): Promise<string> {
    const response = await axios.get<PaymentLinkResponse>(
      `${this.apiUrl}/paymentLink/slot/${slotId}`,
      {
        headers: {
          Authorization: await this.getAuthorizationHeader(),
        },
      }
    );

    if (!response.data?.success || !response.data?.data?.paymentLink) {
      throw new Error("Não foi possível gerar o link de pagamento");
    }

    return response.data.data.paymentLink;
  }

  /**
   * Formata o número de telefone para o padrão internacional
   * @param phone Número de telefone
   * @returns Número formatado
   */
  private formatPhoneNumber(phone: string): string {
    // Remove caracteres não numéricos
    const numericPhone = phone.replace(/\D/g, "");

    // Adiciona o código do país se necessário
    if (!numericPhone.startsWith("55")) {
      return `55${numericPhone}`;
    }
    return numericPhone;
  }

  /**
   * Gera o link de pagamento
   * @param data Dados necessários para gerar o link
   * @returns Link de pagamento
   * @throws Error se houver falha no processo
   */
  async sendPaymentMessage(data: PaymentMessageData): Promise<string> {
    try {
      const slotId = data.slotReference.replace("Slot/", "");
      const paymentLink = await this.getPaymentLinkBySlotId(slotId);
      return paymentLink;
    } catch (error) {
      console.error("Erro ao processar link de pagamento:", error);
      throw error;
    }
  }

  /**
   * Cancela o contrato da extensão marketplace
   * @param contractId ID do contrato a ser cancelado
   * @returns Objeto com status de sucesso e mensagem
   * @throws Error se houver falha no processo de cancelamento
   */
  async cancelMarketplaceContract(
    contractId: string
  ): Promise<{ success: boolean; message: string }> {
    try {
      const response = await axios.post<{ success: boolean; message: string }>(
        `${this.apiUrl}/cancelExtension/${contractId}`,
        {},
        {
          headers: {
            Authorization: await this.getAuthorizationHeader(),
          },
        }
      );

      if (!response.data?.success) {
        throw new Error("Falha ao cancelar extensão Fluxpay");
      }

      return {
        success: true,
        message: "Extensão FluxPay cancelada com sucesso",
      };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error("Erro ao cancelar extensão FluxPay:", error.message);

        if (error.response) {
          // Tratamento específico para erros HTTP
          if (error.response.status === 404) {
            throw new Error("Contrato não encontrado");
          } else if (error.response.status === 400) {
            throw new Error("ID do contrato inválido ou não fornecido");
          }

          console.error("Detalhes do erro:", error.response.data);
          throw new Error(
            `Erro no cancelamento: ${
              error.response.data?.message || error.message
            }`
          );
        }

        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        console.error(
          "Ocorreu um erro desconhecido ao cancelar contrato:",
          error
        );
        throw new Error("Erro desconhecido ao cancelar extensão Fluxpay");
      }
    }
  }

  /**
   * Verifica se uma organização possui extensão marketplace ativa
   * @param organizationId ID da organização a ser verificada
   * @returns Objeto com informações sobre a extensão e ID do contrato se existir
   * @throws Error se houver falha no processo de verificação
   */
  async checkMarketplaceExtension(
    organizationId: string
  ): Promise<MarketplaceExtensionResponse> {
    try {
      const response = await axios.get<MarketplaceExtensionResponse>(
        `${this.apiUrl}/organization/${organizationId}/extension`,
        {
          headers: {
            Authorization: await this.getAuthorizationHeader(),
          },
        }
      );

      // Retorna o objeto com as informações da extensão
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error("Erro ao verificar extensão FluxPay:", error.message);

        if (error.response) {
          // Tratamento específico para erros HTTP
          if (error.response.status === 400) {
            throw new Error("ID da organização inválido ou não fornecido");
          }

          console.error("Detalhes do erro:", error.response.data);
          throw new Error(
            `Erro na verificação: ${
              typeof error.response.data === "object"
                ? error.response.data?.message
                : error.message
            }`
          );
        }

        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        console.error(
          "Ocorreu um erro desconhecido ao verificar extensão FluxPay:",
          error
        );
        throw new Error("Erro desconhecido ao verificar extensão FluxPay");
      }
    }
  }

  /**
   * Verifica se uma organização possui conta vinculada no marketplace
   * @param organizationId ID da organização a ser verificada
   * @returns Objeto com informações sobre a conta vinculada
   * @throws Error se houver falha no processo de verificação
   */
  async linkedAccount(organizationId: string): Promise<LinkedAccountResponse> {
    try {
      const response = await axios.get<LinkedAccountResponse>(
        `${this.apiUrl}/linkedAccount/${organizationId}`,
        {
          headers: {
            Authorization: await this.getAuthorizationHeader(),
          },
        }
      );

      // Retorna o objeto com as informações da conta vinculada
      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error("Erro ao verificar conta vinculada:", error.message);

        if (error.response) {
          // Tratamento específico para erros HTTP
          if (error.response.status === 400) {
            throw new Error("ID da organização inválido ou não fornecido");
          }
          if (error.response.status === 404) {
            throw new Error("Organização não encontrada");
          }

          console.error("Detalhes do erro:", error.response.data);
          throw new Error(
            `Erro na verificação da conta: ${
              typeof error.response.data === "object"
                ? error.response.data?.message
                : error.message
            }`
          );
        }

        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        console.error(
          "Ocorreu um erro desconhecido ao verificar conta vinculada:",
          error
        );
        throw new Error("Erro desconhecido ao verificar conta vinculada");
      }
    }
  }

  /**
   * Gera um link de pagamento para um produto específico
   * @param data Dados do produto e pagamento
   * @returns Link de pagamento
   * @throws Error se houver falha no processo
   */
  async getProductPaymentLink(data: ProductPaymentRequest): Promise<string> {
    try {
      // Preferimos o token de autenticação padrão do legado:
      // - se existir token do usuário -> Authorization: Bearer <token>
      // - se não existir -> gera token externo de aplicação e devolve Bearer
      const authorizationHeader = await getAuthToken();

      const portalConsumerKeyFromEnv =
        import.meta.env.VITE_PORTAL_CONSUMER_KEY as string | undefined;
      const portalKeyToSend =
        portalConsumerKeyFromEnv || endpoints.portalConsumerKey;

      const response = await axios.post<PaymentLinkResponse>(
        `${this.apiUrl}/product/payment-link`,
        { ...data, origin: currentFeatures.defaultApp ? "fluxmed" : "fluxvet" },
        {
          headers: {
            Authorization: authorizationHeader,
            // Backend também aceita `ApiKey : API_KEY` (mensagem 401).
            ApiKey: portalKeyToSend || undefined,
          },
        }
      );

      if (!response.data?.success || !response.data?.data?.paymentLink) {
        throw new Error(
          "Não foi possível gerar o link de pagamento para o produto"
        );
      }

      return response.data.data.paymentLink;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error(
          "Erro ao gerar link de pagamento do produto:",
          error.message
        );
        if (error.response) {
          console.error("Detalhes do erro:", error.response.data);
          throw new Error(
            `Erro na geração do link: ${
              error.response.data?.message || error.message
            }`
          );
        }
        throw new Error(`Erro na solicitação: ${error.message}`);
      } else {
        console.error("Ocorreu um erro desconhecido:", error);
        throw new Error(
          "Erro desconhecido ao gerar link de pagamento do produto"
        );
      }
    }
  }
}

// Instância exportada para uso direto em componentes
export const marketplaceService = new MarketplaceService();
